import React, { useCallback } from 'react';
import { getRequestBodySchema } from '../../../../../common/src/routes/openApiSchemaHelpers';
import { OpenApiRouteResultType } from '../../../../../common/src/routes/openApiTypes';
import { useTranslate } from '../../../mixins/translate/translate';
import { openApiFetchDelete, openApiFetchGet } from '../../../store/openApiFetch';
import { IconRemoveButton } from '../../common/iconButtons/IconRemoveButton';
import {
    CollapsiblePanel,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelRow,
    PanelBody,
    PanelButtonLink,
    PanelButtons,
    PanelTable,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
} from '../../elements';
import { InvestigationPriorityLabel } from '../../utils/InvestigationPriorityLabel';
import { ValidatingJsonSchemaForm } from '../../validatingComponents/ValidatingJsonSchemaForm';
import {
    ValidatingEditPanelSaveButton,
    ValidatingFailureModeDropdown,
    ValidatingFormInputNumberSelect,
    ValidatingFormInputText,
} from '../../validatingElements';

export function SurveyEditFmecaFurtherInvestigations(props: {
    surveyId: string;
    unitId?: undefined | string;
    manifestationId?: undefined | string;
    failureModeId?: undefined | string;
    isFmecaAvailable: boolean;
    header: string;
}) {
    const translate = useTranslate();

    const [items, setItems] = React.useState<OpenApiRouteResultType<'/api/surveys/{surveyId}/fmecaFurtherInvestigations', 'get'>>([]);

    const [state, setState] = React.useState<{
        selectedItem?: undefined | (typeof items)[number];
        formMode: 'none' | 'add' | 'edit';
    }>({
        formMode: 'none',
    });

    const fetchData = useCallback(async () => {
        const result = await openApiFetchGet('/api/surveys/{surveyId}/fmecaFurtherInvestigations', {
            surveyId: props.surveyId,
            unitId: props.unitId,
            manifestationId: props.manifestationId,
            failureModeId: props.failureModeId,
        });

        setItems(result);
    }, [props.surveyId, props.unitId, props.manifestationId, props.failureModeId]);

    React.useEffect(() => {
        void fetchData();
    }, [fetchData]);

    function onRowClick(item: (typeof items)[number]) {
        setState(() => ({ selectedItem: item, formMode: 'edit' }));
    }

    async function removeItem(id: string) {
        await openApiFetchDelete('/api/surveys/{surveyId}/fmecaFurtherInvestigations/{fmecaFurtherInvestigationId}', {
            surveyId: props.surveyId,
            fmecaFurtherInvestigationId: id,
        });

        await fetchData();
    }

    function showAddItem() {
        setState((value) => ({ ...value, ...{ formMode: 'add' } }));
    }

    async function itemSaved() {
        setState(() => ({ formMode: 'none', selectedItem: undefined }));
        await fetchData();
    }

    function handleCancel() {
        setState(() => ({ formMode: 'none', selectedItem: undefined }));
    }

    return (
        <div className="mb20">
            <CollapsiblePanel heading={{ dark: true, text: props.header, initialIsClosed: true }}>
                <PanelBody isForm={true}>
                    <PanelTable>
                        <TableHead>
                            <TableHeadRow>
                                <TableHeader>{translate('common.description')}</TableHeader>
                                <TableHeader>{translate('common.priority')}</TableHeader>
                                <TableHeader>&nbsp;</TableHeader>
                            </TableHeadRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow
                                    key={item.id}
                                    isRowSelected={state.selectedItem && item.id === state.selectedItem.id}
                                    onClick={() => onRowClick(item)}
                                >
                                    <TableData>{item.description}</TableData>
                                    <TableData>
                                        <InvestigationPriorityLabel value={item.priority} />
                                    </TableData>
                                    <TableData>
                                        <IconRemoveButton onClick={async () => removeItem(item.id)} />
                                    </TableData>
                                </TableRow>
                            ))}
                        </TableBody>
                    </PanelTable>

                    {state.formMode === 'none' && (
                        <PanelButtons>
                            <PanelButtonLink
                                onClick={() => showAddItem()}
                                small={true}
                                primary={true}
                                text={translate('common.addItem', translate('fmecaFurtherInvestigation.furtherInvestigation'))}
                            />
                        </PanelButtons>
                    )}

                    {state.formMode !== 'none' && (
                        <ValidatingJsonSchemaForm
                            createSchema={getRequestBodySchema('/api/surveys/{surveyId}/fmecaFurtherInvestigations', 'post')}
                            updateSchema={getRequestBodySchema(
                                '/api/surveys/{surveyId}/fmecaFurtherInvestigations/{fmecaFurtherInvestigationId}',
                                'post',
                            )}
                            createUrl={`/api/surveys/${props.surveyId}/fmecaFurtherInvestigations`}
                            createUrlQueryParameters={{
                                unitId: props.unitId,
                                manifestationId: props.manifestationId,
                                failureModeId: props.failureModeId,
                            }}
                            updateUrl={
                                state.selectedItem ? `/api/surveys/${props.surveyId}/fmecaFurtherInvestigations/${state.selectedItem.id}` : 'idNotSet'
                            }
                            itemUrl={
                                state.selectedItem ? `/api/surveys/${props.surveyId}/fmecaFurtherInvestigations/${state.selectedItem.id}` : 'idNotSet'
                            }
                            id={state.selectedItem ? state.selectedItem.id : undefined}
                            onSubmitted={async () => itemSaved()}
                            subForm={true}
                            defaultItem={{ priority: 1, failureModeId: null }}
                        >
                            <EditPanelRow>
                                <EditPanelCell rowCount={10}>
                                    <ValidatingFormInputText name="description" topLabel={translate('common.description')} />
                                </EditPanelCell>
                                <EditPanelCell rowCount={2}>
                                    <ValidatingFormInputNumberSelect
                                        name="priority"
                                        topLabel={translate('common.priority')}
                                        options={[1, 2, 3].map((i) => ({ value: i, text: translate(`fmecaFurtherInvestigation.priority${i}`) }))}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>

                            {props.isFmecaAvailable && props.failureModeId === undefined && (
                                <EditPanelRow>
                                    <EditPanelCell rowCount={12}>
                                        <ValidatingFailureModeDropdown
                                            name="failureModeId"
                                            surveyId={props.surveyId}
                                            unitId={props.unitId}
                                            manifestationId={props.manifestationId}
                                            failureModeId={props.failureModeId}
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>
                            )}
                            <EditPanelButtons>
                                <EditPanelButton small={true} onClick={() => handleCancel()} text={translate('common.cancel')} />
                                <ValidatingEditPanelSaveButton small={true} />
                            </EditPanelButtons>
                        </ValidatingJsonSchemaForm>
                    )}
                </PanelBody>
            </CollapsiblePanel>
        </div>
    );
}
